<template>
  <div class="border-b-2 border-primary bg-white px-6 py-2">
    <!-- Breadcrumb trail (from route meta) — every segment jumps straight to
         that level, so no more chains of back presses. -->
    <nav v-if="breadcrumbTrail.length" class="flex items-center text-base text-gray-500 pt-1">
      <template v-for="(crumb, index) in breadcrumbTrail">
        <span v-if="index > 0" :key="`sep-${crumb.name}`" class="mx-2 text-gray-400">/</span>
        <a
            :key="crumb.name"
            href="#"
            class="hover:text-primary hover:underline"
            @click.prevent="goToCrumb(crumb)"
        >{{ crumb.label }}</a>
      </template>
      <span class="mx-2 text-gray-400">/</span>
      <span class="text-secondary font-semibold">{{ currentCrumbLabel }}</span>

      <!-- Sibling quick-switch: jump across pages of the same section -->
      <span v-if="siblingRoutes.length" class="relative" v-click-away="closeSiblings">
        <button
            type="button"
            class="ml-1 px-1 text-gray-500 hover:text-primary"
            @click="siblingsOpen = !siblingsOpen"
        >
          &#9662;
        </button>
        <div
            v-if="siblingsOpen"
            class="absolute left-0 mt-1 py-1 w-48 bg-white border border-gray-300 rounded shadow-lg z-50"
        >
          <a
              v-for="sibling in siblingRoutes"
              :key="sibling.name"
              href="#"
              class="block px-4 py-2 text-base text-secondary hover:bg-orange-100"
              @click.prevent="goToSibling(sibling)"
          >{{ sibling.label }}</a>
        </div>
      </span>
    </nav>

    <div class="flex justify-between">
      <div class="flex items-center">
        <!-- Back Button -->
        <button
            v-if="backTo"
            type="button"
            @click="backPressed"
            class="p-2 mr-2 rounded text-secondary border-transparent hover:bg-orange-300 transition-colors duration-150 ease-in-out"
        >
          <svg
              class="h-6 w-6 stroke-current"
              fill="none"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <!-- Header Title and Info -->
        <h1 class="text-secondary text-3xl">{{ title }}</h1>
        <span v-if="info" class="ml-2 text-2xl text-orange-500 mb-1">|</span>
        <span v-if="info" class="ml-2 text-orange-500 text-3xl">{{ info }}</span>

        <!-- Secondary Info Badge -->
        <span
            v-if="secondaryInfo"
            class="ml-4 px-3 py-1 rounded-full font-semibold inline-block"
            :class="[secondaryInfoColor, secondaryInfoFontSize]"
        >
          {{ secondaryInfo }}
        </span>

        <!-- Last Saved Time -->
        <span v-if="lastSavedTime" class="ml-4 flex items-center text-sm text-green-500">
          <svg class="h-6 w-6 fill-current mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          Last saved: {{ lastSavedTime }}
        </span>
      </div>
      <div class="flex items-center">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { goBackSmart } from '@/utils/backNavigation';

export default {
  // Minimal click-away so the sibling dropdown closes without a new dependency.
  directives: {
    'click-away': {
      bind(el, binding) {
        el.__clickAwayHandler__ = (event) => {
          if (!el.contains(event.target)) {
            binding.value();
          }
        };
        document.addEventListener('click', el.__clickAwayHandler__);
      },
      unbind(el) {
        document.removeEventListener('click', el.__clickAwayHandler__);
        delete el.__clickAwayHandler__;
      },
    },
  },
  props: {
    title: String,
    backTo: String,
    enableLogicalBack: Boolean,
    info: String,
    lastSavedTime: String,
    secondaryInfo: String,
    secondaryInfoColor: {
      type: String,
      default: 'text-blue-600'
    },
    secondaryInfoFontSize: {
      type: String,
      default: 'text-xl'
    }
  },
  data() {
    return {
      siblingsOpen: false,
    };
  },
  computed: {
    routesByName() {
      const map = {};
      (this.$router.options.routes || []).forEach((route) => {
        if (route.name) {
          map[route.name] = route;
        }
      });
      return map;
    },
    currentCrumb() {
      return this.$route.meta && this.$route.meta.breadcrumb
          ? this.$route.meta.breadcrumb
          : null;
    },
    currentCrumbLabel() {
      // The entity label the page already passes (PO number, job sheet no...)
      // makes a better leaf crumb than the generic route label.
      if (this.info) {
        return this.info;
      }
      return this.currentCrumb ? this.currentCrumb.label : this.title;
    },
    breadcrumbTrail() {
      // Walk parent names up from the current route (cycle-guarded).
      const trail = [];
      let crumb = this.currentCrumb;
      const seen = new Set([this.$route.name]);
      while (crumb && crumb.parent && !seen.has(crumb.parent)) {
        const parentRoute = this.routesByName[crumb.parent];
        if (!parentRoute || !parentRoute.meta || !parentRoute.meta.breadcrumb) {
          break;
        }
        seen.add(parentRoute.name);
        trail.unshift({ name: parentRoute.name, label: parentRoute.meta.breadcrumb.label });
        crumb = parentRoute.meta.breadcrumb;
      }
      return trail;
    },
    siblingRoutes() {
      // Same-parent pages that need no route params (safe to jump straight to).
      // Mirrors the hub tiles: sections without a tile are marked hidden, and
      // permission-gated sections require one of the user's permissions (same
      // rule as the IsAuthorized component).
      if (!this.currentCrumb || !this.currentCrumb.parent) {
        return [];
      }
      // Quick-switch only on index-style pages: on record pages (any route with
      // params) and create pages the dropdown degrades into a stray "Create"
      // action, so leaf pages get the plain trail instead.
      if (Object.keys(this.$route.params || {}).length > 0) {
        return [];
      }
      if (String(this.$route.name || '').endsWith('-create') || (this.currentCrumb.label || '') === 'Create') {
        return [];
      }
      const userPermissions = this.$store.state.userPermissions || [];
      return (this.$router.options.routes || [])
          .filter((route) => {
            const crumb = route.meta && route.meta.breadcrumb;
            if (!route.name || route.name === this.$route.name || !crumb) {
              return false;
            }
            if (crumb.parent !== this.currentCrumb.parent || route.path.includes(':')) {
              return false;
            }
            if (crumb.hidden) {
              return false;
            }
            if (crumb.permissions && !userPermissions.some(p => crumb.permissions.includes(p))) {
              return false;
            }
            return true;
          })
          .map(route => ({ name: route.name, label: route.meta.breadcrumb.label }));
    },
  },
  methods: {
    goToCrumb(crumb) {
      this.siblingsOpen = false;
      // Parents needing params (e.g. dispatch -> its sales order) reuse the
      // current route's params, which are always a superset on child pages.
      this.$router.push({ name: crumb.name, params: this.$route.params }).catch(() => {});
    },
    goToSibling(sibling) {
      this.siblingsOpen = false;
      this.$router.push({ name: sibling.name }).catch(() => {});
    },
    closeSiblings() {
      this.siblingsOpen = false;
    },
    // UNIFORM back rule (replaces the old per-page enableLogicalBack/backTo
    // split, which sent people to inconsistent places). The logic lives in
    // utils/backNavigation.js and is shared with the "save then leave" flows.
    backPressed: function () {
      goBackSmart(this.$router, this.$route, this.backTo);
    }
  }
};
</script>
